import React from "react";
import ReactDOM from "react-dom/client";
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isAuthenticated } from "@/lib/api";
import { LoginPage } from "@/routes/login";
import { DashboardPage } from "@/routes/dashboard";
import { ConversationsPage } from "@/routes/conversations";
import { VectorsPage } from "@/routes/vectors";
import { SyncPage } from "@/routes/sync";
import { SettingsPage } from "@/routes/settings";
import { SourcesPage } from "@/routes/sources";
import { EvalsPage } from "@/routes/evals";
import { TicketsPage } from "@/routes/tickets";
import { IncentivesPage } from "@/routes/incentives";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// Root route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Login route with optional apiMode query param
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    apiMode: search.apiMode as string | undefined,
  }),
});

// Protected routes wrapper
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: "/login", search: { apiMode: undefined } });
    }
  },
  component: () => <Outlet />,
});

// Dashboard route
const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

// Conversations route
const conversationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/conversations",
  component: ConversationsPage,
});

// Vectors route
const vectorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/vectors",
  component: VectorsPage,
});

// Sync route
const syncRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/sync",
  component: SyncPage,
});

// Settings route
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: SettingsPage,
});

// Sources route
const sourcesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/sources",
  component: SourcesPage,
});

// Evals route
const evalsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/evals",
  component: EvalsPage,
});

// Tickets route
const ticketsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/tickets",
  component: TicketsPage,
});

// Incentives route
const incentivesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/incentives",
  component: IncentivesPage,
});

// Route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    ticketsRoute,
    incentivesRoute,
    conversationsRoute,
    vectorsRoute,
    syncRoute,
    sourcesRoute,
    settingsRoute,
    evalsRoute,
  ]),
]);

// Router
const router = createRouter({ routeTree });

// Type declarations
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
);

